import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Heart, ShieldCheck, TestTube, Pill } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';

const HIVSTBBIPartyingPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const topics = [
    {
      id: 'hiv-party-spaces',
      title: 'HIV & STBBIs in Party Spaces',
      emoji: '🌈',
      icon: Heart,
      color: 'from-pink-500 to-rose-500',
      content: {
        intro: 'HIV and STBBIs exist in party spaces, but they also exist everywhere else. Partying doesn\'t create HIV, but some party settings can increase risk.',
        factors: [
          'Multiple partners',
          'Longer sessions',
          'Bareback sex',
          'Less condom use',
          'Mixing substances',
          'Lower barriers and higher impulsivity'
        ],
        message: 'PSP doesn\'t shame people for having sex or partying. We focus on real-life risk + real-life tools to stay safer.'
      }
    },
    {
      id: 'prep-partying',
      title: 'PrEP & Partying',
      emoji: '💊',
      icon: Pill,
      color: 'from-blue-500 to-cyan-500',
      content: {
        intro: 'PrEP works if you take it properly. Partying can make it harder to remember daily doses, keep routines, and plan ahead.',
        challenges: [
          'Forgetting doses during long party sessions',
          'Sleeping all day after partying',
          'Disrupted daily routines',
          'Crashing and losing track of time'
        ],
        tips: [
          'Set daily alarms for your PrEP',
          'Keep PrEP in your bag when partying',
          'Take it at the same time every day',
          'If you vomit within 1 hour, take another pill',
          'Consider long-acting injection if you miss doses often'
        ],
        footer: 'P = P: Protection = pills + routine + self-care'
      }
    },
    {
      id: 'living-with-hiv',
      title: 'Partying While Living with HIV',
      emoji: '💙',
      icon: ShieldCheck,
      color: 'from-purple-500 to-indigo-500',
      content: {
        intro: 'People living with HIV party, date, and have sex like everyone else. Treatment works. U=U (Undetectable = Untransmittable) is real.',
        facts: [
          'U=U means you cannot transmit HIV when undetectable',
          'Taking meds consistently keeps you healthy',
          'Partying can affect med routines',
          'Sleep and hydration matter for your health'
        ],
        support: [
          'Set medication reminders',
          'Keep meds with you at parties',
          'Plan rest and recovery after sessions',
          'Don\'t let partying disrupt your care routine',
          'Protect your health while enjoying yourself'
        ]
      }
    },
    {
      id: 'drug-interactions',
      title: 'Party Drugs & HIV Meds',
      emoji: '⚗️',
      icon: TestTube,
      color: 'from-orange-500 to-red-500',
      content: {
        intro: 'Some party drugs can interact with HIV meds or affect the body in ways that increase side effects, stress the heart or liver, or mess with routine.',
        biggestRisk: 'The biggest risk is not always the drug interaction — sometimes it\'s forgetting doses while partying or crashing.',
        tips: [
          'Check interactions with your doctor or pharmacist',
          'Plan med doses around party schedule',
          'Keep medications nearby',
          'Choose rest and recovery after sessions',
          'Hydrate and eat to support your body'
        ]
      }
    },
    {
      id: 'testing',
      title: 'Testing & Getting Treated',
      emoji: '🧬',
      icon: TestTube,
      color: 'from-green-500 to-emerald-500',
      content: {
        intro: 'Regular testing is harm reduction. Partying can mean more partners, less condom use, and more risk-taking.',
        testingTools: [
          'Condoms',
          'PrEP (for HIV prevention)',
          'TasP / U=U (Treatment as Prevention)',
          'Regular testing',
          'Early treatment'
        ],
        getakit: 'Use GetAKit for free, discreet HIV and STI testing kits delivered to your home.',
        message: 'No single tool is "perfect". Harm reduction is about layers of protection.'
      }
    },
    {
      id: 'bug-chasing',
      title: "Bug Chasing: Why It's Harmful",
      emoji: '🛑',
      icon: Heart,
      color: 'from-red-500 to-pink-500',
      content: {
        intro: "Bug chasing is when someone intentionally tries to get HIV. Sometimes people ask someone living with HIV to stop taking medication so they can become infected. It's not a game or fantasy - it's dangerous.",
        whyHarmful: [
          'HIV is a serious medical condition',
          'Stopping medication puts people\'s health at risk',
          'Can hurt mental health',
          'Makes people living with HIV feel stigmatized',
          'Increases risk of spreading HIV and other infections'
        ],
        forPWH: [
          'You do not owe anyone your health',
          'Your medication protects you and your partners',
          'Your lived experience deserves respect and dignity'
        ],
        message: 'Living with HIV is about care, respect, and safety for yourself and others.'
      }
    }
  ];

  const selectedTopic = searchParams.get('topic');
  const selectedContent = topics.find(t => t.id === selectedTopic);

  return (
    <div className="min-h-screen flex flex-col relative z-10">
      <header className="glass-morphism sticky top-0 z-50 border-b">
        <div className="max-w-2xl mx-auto px-6 py-4">
          <div className="flex items-center">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => selectedTopic ? setSearchParams({}) : navigate('/')}
              data-testid="back-button"
              className="mr-4 text-[rgb(var(--text-secondary))] hover:text-[rgb(var(--primary))]"
            >
              <ArrowLeft size={24} weight="duotone" />
            </Button>
            <div className="flex items-center space-x-2">
              <span className="text-3xl emoji-bounce">💙</span>
              <h1 className="text-2xl font-bold text-gradient">
                {selectedTopic ? selectedContent?.title : 'HIV & STBBI + Partying'}
              </h1>
            </div>
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-2xl mx-auto w-full px-6 py-8 pb-24">
        {!selectedTopic ? (
          <>
            <div className="mb-8 p-6 party-gradient rounded-3xl text-white text-center">
              <div className="text-6xl mb-3">🏳️‍🌈</div>
              <h2 className="text-2xl font-bold mb-3">Real Talk About HIV & Partying</h2>
              <p className="text-white/90 leading-relaxed">
                You can party AND protect your health. Learn about PrEP, U=U, safer sex, and staying healthy in party spaces. No shame, just facts. 💜
              </p>
            </div>

            <div className="space-y-4">
              {topics.map((topic) => {
                const Icon = topic.icon;
                return (
                  <button
                    key={topic.id}
                    onClick={() => setSearchParams({ topic: topic.id })}
                    data-testid={`topic-${topic.id}`}
                    className="w-full group relative bg-[rgb(var(--surface))] hover:bg-[rgb(var(--surface-elevated))] rounded-3xl p-6 border border-white/10 text-left transition-all duration-200 hover:border-[rgb(var(--primary))]/50 btn-hover overflow-hidden"
                  >
                    <div className={`absolute inset-0 bg-gradient-to-br ${topic.color} opacity-0 group-hover:opacity-10 transition-opacity`}></div>
                    <div className="relative z-10 flex items-center">
                      <div className="text-5xl mr-4 emoji-bounce">{topic.emoji}</div>
                      <div className="flex-1">
                        <h3 className="text-xl font-bold text-[rgb(var(--text-primary))] mb-1">
                          {topic.title}
                        </h3>
                      </div>
                      <Icon size={24} weight="duotone" className="text-[rgb(var(--primary))] group-hover:scale-110 transition-transform" />
                    </div>
                  </button>
                );
              })}
            </div>

            <div className="mt-8 p-6 bg-gradient-to-r from-[rgb(var(--primary))]/10 to-[rgb(var(--secondary))]/10 rounded-3xl border border-[rgb(var(--primary))]/20">
              <h3 className="text-lg font-bold text-[rgb(var(--text-primary))] mb-3">💡 Quick Facts</h3>
              <ul className="space-y-2 text-sm text-[rgb(var(--text-secondary))]">
                <li>• U=U is real: Undetectable = Untransmittable</li>
                <li>• PrEP is 99% effective when taken correctly</li>
                <li>• Party drugs don't cancel PrEP or HIV meds</li>
                <li>• Testing is part of taking care of yourself</li>
                <li>• You deserve pleasure AND safety</li>
              </ul>
            </div>
          </>
        ) : (
          <div className="space-y-6">
            <div className={`p-6 bg-gradient-to-br ${selectedContent.color} rounded-3xl text-white text-center`}>
              <div className="text-6xl mb-3">{selectedContent.emoji}</div>
              <h2 className="text-2xl font-bold">{selectedContent.title}</h2>
            </div>

            <div className="p-6 bg-[rgb(var(--surface))] rounded-2xl border border-white/10">
              <p className="text-[rgb(var(--text-secondary))] leading-relaxed">
                {selectedContent.content.intro}
              </p>
            </div>

            {selectedContent.content.factors && (
              <div>
                <h3 className="text-lg font-bold text-[rgb(var(--text-primary))] mb-3">
                  Risk Factors
                </h3>
                <ul className="space-y-2">
                  {selectedContent.content.factors.map((factor, idx) => (
                    <li key={idx} className="flex items-start p-3 bg-[rgb(var(--surface))] rounded-xl border border-white/5">
                      <span className="text-[rgb(var(--primary))] mr-2">•</span>
                      <span className="text-sm text-[rgb(var(--text-secondary))]">{factor}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {selectedContent.content.challenges && (
              <div>
                <h3 className="text-lg font-bold text-[rgb(var(--text-primary))] mb-3">
                  Common Challenges
                </h3>
                <ul className="space-y-2">
                  {selectedContent.content.challenges.map((challenge, idx) => (
                    <li key={idx} className="flex items-start p-3 bg-[rgb(var(--danger))]/10 border border-[rgb(var(--danger))]/20 rounded-xl">
                      <span className="text-[rgb(var(--danger))] mr-2">⚠️</span>
                      <span className="text-sm text-[rgb(var(--text-secondary))]">{challenge}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {selectedContent.content.facts && (
              <div>
                <h3 className="text-lg font-bold text-[rgb(var(--text-primary))] mb-3">
                  Key Facts
                </h3>
                <ul className="space-y-2">
                  {selectedContent.content.facts.map((fact, idx) => (
                    <li key={idx} className="flex items-start p-3 bg-[rgb(var(--primary))]/10 border border-[rgb(var(--primary))]/20 rounded-xl">
                      <span className="text-[rgb(var(--primary))] mr-2">✓</span>
                      <span className="text-sm text-[rgb(var(--text-secondary))]">{fact}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {selectedContent.content.tips && (
              <div>
                <h3 className="text-lg font-bold text-[rgb(var(--text-primary))] mb-3">
                  💚 Harm Reduction Tips
                </h3>
                <ul className="space-y-2">
                  {selectedContent.content.tips.map((tip, idx) => (
                    <li key={idx} className="flex items-start p-3 bg-[rgb(var(--success))]/10 border border-[rgb(var(--success))]/20 rounded-xl">
                      <span className="text-[rgb(var(--success))] mr-2 text-lg">✓</span>
                      <span className="text-sm text-[rgb(var(--text-secondary))]">{tip}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {selectedContent.content.support && (
              <div>
                <h3 className="text-lg font-bold text-[rgb(var(--text-primary))] mb-3">
                  Support Tips
                </h3>
                <ul className="space-y-2">
                  {selectedContent.content.support.map((item, idx) => (
                    <li key={idx} className="flex items-start p-3 bg-[rgb(var(--primary))]/10 border border-[rgb(var(--primary))]/20 rounded-xl">
                      <span className="text-[rgb(var(--primary))] mr-2">💙</span>
                      <span className="text-sm text-[rgb(var(--text-secondary))]">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {selectedContent.content.testingTools && (
              <div>
                <h3 className="text-lg font-bold text-[rgb(var(--text-primary))] mb-3">
                  Testing & Protection Tools
                </h3>
                <ul className="space-y-2">
                  {selectedContent.content.testingTools.map((tool, idx) => (
                    <li key={idx} className="flex items-start p-3 bg-[rgb(var(--surface))] rounded-xl border border-white/5">
                      <span className="text-[rgb(var(--success))] mr-2">✓</span>
                      <span className="text-sm text-[rgb(var(--text-secondary))]">{tool}</span>
                    </li>
                  ))}
                </ul>
                {selectedContent.content.getakit && (
                  <div className="mt-3 p-4 bg-[rgb(var(--accent))]/10 border border-[rgb(var(--accent))]/20 rounded-xl">
                    <p className="text-sm text-[rgb(var(--text-secondary))]">
                      <strong className="text-[rgb(var(--accent))]">GetAKit:</strong> {selectedContent.content.getakit}
                    </p>
                  </div>
                )}
              </div>
            )}

            {selectedContent.content.whyHarmful && (
              <div>
                <h3 className="text-lg font-bold text-[rgb(var(--danger))] mb-3">
                  Why It's Harmful
                </h3>
                <ul className="space-y-2">
                  {selectedContent.content.whyHarmful.map((reason, idx) => (
                    <li key={idx} className="flex items-start p-3 bg-[rgb(var(--danger))]/10 border border-[rgb(var(--danger))]/20 rounded-xl">
                      <span className="text-[rgb(var(--danger))] mr-2">⚠️</span>
                      <span className="text-sm text-[rgb(var(--text-secondary))]">{reason}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {selectedContent.content.forPWH && (
              <div className="p-6 bg-gradient-to-r from-[rgb(var(--primary))]/10 to-[rgb(var(--secondary))]/10 rounded-2xl border border-[rgb(var(--primary))]/20">
                <h3 className="text-lg font-bold text-[rgb(var(--text-primary))] mb-3">
                  For People Living with HIV
                </h3>
                <ul className="space-y-2">
                  {selectedContent.content.forPWH.map((message, idx) => (
                    <li key={idx} className="text-sm text-[rgb(var(--text-secondary))]">
                      • {message}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {(selectedContent.content.message || selectedContent.content.footer) && (
              <div className="p-6 bg-gradient-to-r from-[rgb(var(--primary))]/10 to-[rgb(var(--secondary))]/10 rounded-2xl border border-[rgb(var(--primary))]/20 text-center">
                <p className="text-[rgb(var(--text-primary))] font-bold">
                  {selectedContent.content.message || selectedContent.content.footer}
                </p>
              </div>
            )}

            {selectedContent.content.biggestRisk && (
              <div className="p-4 bg-[rgb(var(--danger))]/10 border border-[rgb(var(--danger))]/20 rounded-xl">
                <p className="text-sm text-[rgb(var(--text-secondary))]">
                  <strong className="text-[rgb(var(--danger))]">Important:</strong> {selectedContent.content.biggestRisk}
                </p>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default HIVSTBBIPartyingPage;
